
const AchievementsBadges = () => {
  const achievements = [
    {
      id: 1,
      title: 'First Steps',
      description: 'Complete your first lesson',
      icon: 'ri-footprint-line',
      color: 'green',
      earned: true,
      earnedDate: '2024-01-15'
    },
    {
      id: 2,
      title: 'Week Warrior',
      description: 'Study for 7 consecutive days',
      icon: 'ri-fire-line',
      color: 'orange',
      earned: true,
      earnedDate: '2024-01-22'
    },
    {
      id: 3,
      title: 'Grammar Master',
      description: 'Complete 10 grammar exercises',
      icon: 'ri-book-line',
      color: 'blue',
      earned: true,
      earnedDate: '2024-01-28'
    },
    {
      id: 4,
      title: 'Vocabulary Builder',
      description: 'Learn 100 new words',
      icon: 'ri-chat-3-line',
      color: 'purple',
      earned: true,
      earnedDate: '2024-02-05'
    },
    {
      id: 5,
      title: 'Speaking Star',
      description: 'Complete 5 pronunciation exercises',
      icon: 'ri-mic-line',
      color: 'pink',
      earned: true,
      earnedDate: '2024-02-10'
    },
    {
      id: 6,
      title: 'Listening Pro',
      description: 'Complete 10 listening exercises',
      icon: 'ri-headphone-line',
      color: 'indigo',
      earned: false,
      progress: 7,
      total: 10
    },
    {
      id: 7,
      title: 'Month Champion',
      description: 'Study for 30 consecutive days',
      icon: 'ri-trophy-line',
      color: 'yellow',
      earned: false,
      progress: 23,
      total: 30
    },
    {
      id: 8,
      title: 'Perfect Score',
      description: 'Get 100% on any exercise',
      icon: 'ri-star-line',
      color: 'red',
      earned: false,
      progress: 0,
      total: 1
    },
    {
      id: 9,
      title: 'Social Learner',
      description: 'Book your first private lesson',
      icon: 'ri-video-chat-line',
      color: 'teal',
      earned: false,
      progress: 0,
      total: 1
    }
  ];

  const getColorClasses = (color: string, earned: boolean) => {
    if (!earned) return 'bg-gray-200 text-gray-400';
    
    const colorMap = {
      green: 'bg-green-500 text-white',
      orange: 'bg-orange-500 text-white',
      blue: 'bg-blue-500 text-white',
      purple: 'bg-purple-500 text-white',
      pink: 'bg-pink-500 text-white',
      indigo: 'bg-indigo-500 text-white',
      yellow: 'bg-yellow-500 text-white',
      red: 'bg-red-500 text-white',
      teal: 'bg-teal-500 text-white',
    };
    return colorMap[color as keyof typeof colorMap];
  };

  const earnedCount = achievements.filter(a => a.earned).length;

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Achievements & Badges
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              Celebrate your milestones and unlock new badges as you progress
            </p>
            <div className="inline-flex items-center space-x-2 bg-white rounded-full px-6 py-3 shadow-sm">
              <i className="ri-trophy-line text-yellow-500 text-xl"></i>
              <span className="font-semibold text-gray-900">
                {earnedCount} of {achievements.length} badges earned
              </span>
            </div>
          </div>

          {/* Achievements Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {achievements.map((achievement) => (
              <div 
                key={achievement.id} 
                className={`bg-white rounded-xl p-6 shadow-sm transition-all hover:shadow-md ${
                  achievement.earned ? 'ring-2 ring-green-200' : ''
                }`}
              >
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    getColorClasses(achievement.color, achievement.earned)
                  }`}>
                    <i className={`${achievement.icon} text-2xl`}></i>
                  </div>
                  
                  <h3 className={`text-lg font-semibold mb-2 ${
                    achievement.earned ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {achievement.title}
                  </h3>
                  
                  <p className={`text-sm mb-4 ${
                    achievement.earned ? 'text-gray-600' : 'text-gray-400'
                  }`}>
                    {achievement.description}
                  </p>

                  {achievement.earned ? (
                    <div className="flex items-center justify-center space-x-2 text-green-600">
                      <i className="ri-checkbox-circle-fill"></i>
                      <span className="text-sm font-medium">
                        Earned on {new Date(achievement.earnedDate!).toLocaleDateString()}
                      </span>
                    </div>
                  ) : (
                    <div>
                      {achievement.progress !== undefined && achievement.total !== undefined ? (
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-gray-500">Progress</span>
                            <span className="text-xs text-gray-500">
                              {achievement.progress}/{achievement.total}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${(achievement.progress / achievement.total) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Not earned yet</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Next Achievement Preview */}
          <div className="mt-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Next Achievement</h3>
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-headphone-line text-xl"></i>
              </div>
              <div>
                <h4 className="font-semibold">Listening Pro</h4>
                <p className="text-blue-100">Complete 10 listening exercises</p>
              </div>
            </div>
            <div className="max-w-xs mx-auto">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">Progress</span>
                <span className="text-sm">7/10</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2">
                <div className="bg-white h-2 rounded-full" style={{ width: '70%' }}></div>
              </div>
              <p className="text-sm text-blue-100 mt-2">3 more exercises to go!</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AchievementsBadges;
